import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Package, ShoppingCart, FileText, User, Bell, LogOut, AlertTriangle } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AiChatWidget } from '@/components/AiChatWidget';

const items = [
  { to: '/app', icon: Home, label: 'Home', end: true },
  { to: '/app/orders', icon: ShoppingCart, label: 'Orders' },
  { to: '/app/catalog', icon: Package, label: 'Catalog' },
  { to: '/app/invoices', icon: FileText, label: 'Invoices' },
  { to: '/app/profile', icon: User, label: 'Profile' },
];

export const CustomerLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const nav = useNavigate();
  const load = () => api.get('/notifications').then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="portal-customer min-h-screen bg-background pb-20">
      <header className="sticky top-0 z-20 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="Mobile Fuel Kart" className="h-7" />
          </div>
          <div className="flex items-center gap-1">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-destructive text-[10px] text-white flex items-center justify-center">{unread}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader><SheetTitle>Notifications</SheetTitle></SheetHeader>
                <div className="mt-4 space-y-2 overflow-y-auto">
                  {notifs.length === 0 && <p className="text-sm text-muted-foreground">No notifications yet.</p>}
                  {notifs.map((n) => {
                    const isReminder = n.type === 'credit_limit' || n.type === 'payment_overdue';
                    const go = () => {
                      api.post(`/notifications/${n.id}/read`).then(load);
                      if (isReminder) nav('/app/invoices');
                      else if (n.order_id) nav(`/app/track/${n.order_id}`);
                    };
                    return (
                      <div key={n.id}
                        className={`p-3 rounded-lg border cursor-pointer ${isReminder ? 'border-l-4 border-l-destructive bg-destructive/5' : n.read ? 'bg-card' : 'bg-muted'}`}
                        onClick={go} data-testid={`notification-${n.type || 'info'}`}>
                        <p className="text-sm font-medium flex items-center gap-1.5">
                          {isReminder && <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />}{n.title}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                        {isReminder && <p className="text-xs font-medium text-primary mt-1">Tap to view invoices & pay</p>}
                      </div>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
            <ThemeToggle />
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button"><LogOut className="h-5 w-5" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-4">{children}</main>
      <AiChatWidget mode="customer_support" />
      <nav className="fixed bottom-0 inset-x-0 z-20 bg-card border-t border-border">
        <div className="max-w-2xl mx-auto grid grid-cols-5 h-16">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-xs ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}
              data-testid={`nav-${it.label.toLowerCase()}`}>
              <it.icon className="h-5 w-5" />{it.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
